import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  AppIcon,
  FavoriteIcon,
  ApiKey,
  UserSettings,
  SearchHistory,
  AppState,
  CountryCode,
  COUNTRIES,
  DownloadProgress,
} from '../types';

// 默认设置
const DEFAULT_SETTINGS: UserSettings = {
  theme: 'auto',
  downloadFormat: 'png',
  downloadQuality: 0.9,
  autoSaveFavorites: true,
  language: 'zh',
  defaultCountry: 'us',
  apiKeys: [],
};

interface AppStore extends AppState {
  // 搜索相关操作
  setSearchQuery: (query: string) => void;
  setSearchResults: (results: AppIcon[]) => void;
  clearSearchResults: () => void;
  
  // 国家/地区相关操作
  setCurrentCountry: (country: CountryCode) => void;
  
  // 收藏相关操作
  addToFavorites: (icon: AppIcon) => void;
  removeFromFavorites: (trackId: number) => void;
  isFavorite: (trackId: number) => boolean;
  clearFavorites: () => void;
  
  // API密钥相关操作
  addApiKey: (apiKey: Omit<ApiKey, 'id' | 'createdAt'>) => void;
  removeApiKey: (id: string) => void;
  updateApiKeyUsage: (id: string, usage: number) => void;
  getActiveApiKey: () => ApiKey | undefined;
  
  // 设置相关操作
  updateSettings: (settings: Partial<UserSettings>) => void;
  
  // 下载进度相关操作
  addDownloadProgress: (progress: DownloadProgress) => void;
  updateDownloadProgress: (id: string, progress: Partial<DownloadProgress>) => void;
  removeDownloadProgress: (id: string) => void;
  
  // 加载和错误状态
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

// 创建应用状态存储
export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // 初始状态
      isLoading: false,
      error: null,
      currentCountry: 'us',
      searchQuery: '',
      searchResults: [],
      favorites: [],
      apiKeys: [],
      settings: DEFAULT_SETTINGS,
      downloadProgress: [],

      // 搜索相关操作
      setSearchQuery: (query) => set({ searchQuery: query }),
      setSearchResults: (results) => set({ searchResults: results }),
      clearSearchResults: () => set({ searchResults: [] }),

      // 国家/地区相关操作
      setCurrentCountry: (country) => set({ currentCountry: country }),

      // 收藏相关操作
      addToFavorites: (icon) => {
        const { favorites, settings } = get();
        const existingIndex = favorites.findIndex(fav => fav.trackId === icon.trackId);
        
        if (existingIndex !== -1) {
          return; // 已经收藏过了
        }

        const newFavorite: FavoriteIcon = {
          id: `fav_${Date.now()}_${icon.trackId}`,
          trackId: icon.trackId,
          trackName: icon.trackName,
          artworkUrl: icon.artworkUrl512,
          artistName: icon.artistName,
          country: icon.country as CountryCode,
          addedAt: Date.now(),
          tags: [icon.primaryGenreName || '其他'].filter(Boolean),
        };

        set({ favorites: [...favorites, newFavorite] });
      },

      removeFromFavorites: (trackId) => {
        const { favorites } = get();
        set({ favorites: favorites.filter(fav => fav.trackId !== trackId) });
      },

      isFavorite: (trackId) => {
        const { favorites } = get();
        return favorites.some(fav => fav.trackId === trackId);
      },

      clearFavorites: () => set({ favorites: [] }),

      // API密钥相关操作
      addApiKey: (apiKey) => {
        const { settings } = get();
        const newApiKey: ApiKey = {
          ...apiKey,
          id: `key_${Date.now()}`,
          createdAt: Date.now(),
        };

        const updatedApiKeys = [...settings.apiKeys, newApiKey];
        set({
          settings: {
            ...settings,
            apiKeys: updatedApiKeys,
          },
        });
      },

      removeApiKey: (id) => {
        const { settings } = get();
        const updatedApiKeys = settings.apiKeys.filter(key => key.id !== id);
        set({
          settings: {
            ...settings,
            apiKeys: updatedApiKeys,
          },
        });
      },

      updateApiKeyUsage: (id, usage) => {
        const { settings } = get();
        const updatedApiKeys = settings.apiKeys.map(key =>
          key.id === id ? { ...key, usage } : key
        );
        set({
          settings: {
            ...settings,
            apiKeys: updatedApiKeys,
          },
        });
      },

      getActiveApiKey: () => {
        const { settings } = get();
        return settings.apiKeys.find(key => key.isActive);
      },

      // 设置相关操作
      updateSettings: (newSettings) => {
        const { settings } = get();
        set({
          settings: {
            ...settings,
            ...newSettings,
          },
        });
      },

      // 下载进度相关操作
      addDownloadProgress: (progress) => {
        const { downloadProgress } = get();
        set({ downloadProgress: [...downloadProgress, progress] });
      },

      updateDownloadProgress: (id, progress) => {
        const { downloadProgress } = get();
        const updatedProgress = downloadProgress.map(item =>
          item.id === id ? { ...item, ...progress } : item
        );
        set({ downloadProgress: updatedProgress });
      },

      removeDownloadProgress: (id) => {
        const { downloadProgress } = get();
        set({ downloadProgress: downloadProgress.filter(item => item.id !== id) });
      },

      // 加载和错误状态
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'app-store', // 存储键名
      storage: createJSONStorage(() => localStorage), // 使用localStorage
      partialize: (state) => ({
        // 只持久化部分状态
        currentCountry: state.currentCountry,
        favorites: state.favorites,
        settings: state.settings,
      }),
    }
  )
);

// 搜索历史存储
export const useSearchHistory = create<SearchHistory>()(
  persist(
    () => ({
      queries: [],
      maxSize: 20,
    }),
    {
      name: 'search-history',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// 选择器函数
export const selectSearchState = (state: AppStore) => ({
  searchQuery: state.searchQuery,
  searchResults: state.searchResults,
  currentCountry: state.currentCountry,
  isLoading: state.isLoading,
  error: state.error,
});

export const selectFavoritesState = (state: AppStore) => ({
  favorites: state.favorites,
  addToFavorites: state.addToFavorites,
  removeFromFavorites: state.removeFromFavorites,
  isFavorite: state.isFavorite,
  clearFavorites: state.clearFavorites,
});

export const selectApiKeysState = (state: AppStore) => ({
  apiKeys: state.settings.apiKeys,
  addApiKey: state.addApiKey,
  removeApiKey: state.removeApiKey,
  updateApiKeyUsage: state.updateApiKeyUsage,
  getActiveApiKey: state.getActiveApiKey,
});

export const selectSettingsState = (state: AppStore) => ({
  settings: state.settings,
  updateSettings: state.updateSettings,
});

export const selectDownloadState = (state: AppStore) => ({
  downloadProgress: state.downloadProgress,
  addDownloadProgress: state.addDownloadProgress,
  updateDownloadProgress: state.updateDownloadProgress,
  removeDownloadProgress: state.removeDownloadProgress,
});

export default useAppStore;
